import React from "react";
import { reject } from "lodash";
import {
  Table,
  Pager,
  Loading,
  Checkbox,
  CardNotification,
  TableColumn,
} from "@foris/avocado-suite";
import { Instructor } from "@models/ISchema";
import { Icon } from "@foris/avocado-icons";
import { getColumnCell, getLinkCell } from "@common/utils/table.util";
import css from "./advanceSearch.module.scss";
import { useTranslation } from "react-i18next";
import useNavigationUrl from "@common/hooks/useNavigationUrl";

interface AdvanceTable {
  loading: boolean;
  data: Instructor[];
  size: number;
  total: number;
  page: number;
  selected: Instructor[];
  setSelected: (value: Instructor[]) => void;
  onPageChange: (page: number) => void;
}

const AdvanceSearch: React.FC<AdvanceTable> = (props: AdvanceTable) => {
  const { loading, data, size, total, page, onPageChange, setSelected, selected } = props;
  const { t } = useTranslation();
  const { editorUrl } = useNavigationUrl();

  const renderCheck = (instructor: Instructor) => {
    const findSelected = selected?.find(value => value.id === instructor?.id);
    const onChange = () => {
      let cloneSelected = selected ? [...selected] : [];
      if (!findSelected) cloneSelected.push(instructor);
      if (findSelected) cloneSelected = reject(cloneSelected, value => value.id === instructor?.id);
      setSelected(cloneSelected);
    };

    return <Checkbox checked={!!findSelected} disabled={!instructor?.usable} onChange={onChange} />;
  };

  const columns = [
    {
      key: "id",
      header: "",
      renderCell: (row: any) => renderCheck(row),
    },
    {
      key: "instructor",
      header: t("assignation-edit.advanced-search.instructor.table-columns.instructor"),
      renderCell: ({ code, id, name }: Instructor) =>
        getLinkCell(editorUrl("instructor", id), null, `${code}, ${name}`),
    },
    {
      key: "priority",
      header: t("assignation-edit.advanced-search.instructor.table-columns.priority"),
      renderCell: (row: any) => getColumnCell(row?.priority ?? "-"),
    },
    {
      header: t("search-results.dimension.instructor.table.header.type-contract"),
      renderCell: ({ contract }: Instructor) => getColumnCell(contract),
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "200px" },
      },
    },
    {
      key: "averagePerIntervalBlocks",
      header: t("assignation-edit.advanced-search.instructor.table-columns.assigned-blocks"),
      renderCell: (row: any) =>
        getColumnCell(row?.assignment ? `${row?.assignment?.averagePerIntervalBlocks}%` : "-"),
    },
    {
      key: "blocks",
      header: t("assignation-edit.advanced-search.instructor.table-columns.total-assigned-blocks"),
      renderCell: (row: any) => getColumnCell(row?.assignment?.blocks ?? "-"),
    },
    {
      key: "usagePercentage",
      header: t("assignation-edit.advanced-search.instructor.table-columns.usage"),
      renderCell: (row: any) =>
        getColumnCell(row?.assignment ? `${row?.assignment.usagePercentage}%` : "-"),
    },
    {
      key: "usable",
      header: t("assignation-edit.advanced-search.instructor.table-columns.usable"),
      renderCell: (row: any) => <Icon name={!!row?.usable ? "check" : "close"} />,
    },
  ];

  if (loading && !data) {
    return <Loading />;
  }

  if (total === 0)
    return (
      <section className={css.cardState}>
        <CardNotification
          state="info"
          title={t("assignation-edit.advanced-search.notifications.empty-results.title")}
          outlined
        >
          {t("assignation-edit.advanced-search.notifications.empty-results.instructor-message")}
        </CardNotification>
      </section>
    );

  return (
    <div className={css.advanceTable}>
      {loading && <Loading />}

      <Table columns={columns as TableColumn[]} data={data} />

      {!!total && (
        <Pager
          classname={{
            global: css.advanceTable_pager,
          }}
          size={size}
          total={total}
          page={page}
          onChange={onPageChange}
        />
      )}
    </div>
  );
};

export default AdvanceSearch;
